/**
 * AtlasCredentialFormAdapter — Flow Studio's implementation of @bizfirst/ai-agent-admin-ui's
 * pluggable `CredentialFormRenderer`/`CredentialOAuthConnect` extension points.
 *
 * Restores the original Atlas-Forms-driven dynamic per-credential-type form and OAuth2 popup
 * connect flow that the 2026-08-19/20 refactor (commit b17411167) deleted from
 * CredentialDialog.tsx/CredentialSection.tsx/CredentialSelector.tsx when those components were
 * replaced by `@bizfirst/ai-agent-admin-ui`'s plain-HTML `CredentialEditor`. Lives in Flow
 * Studio's own workspace (not the shared package) because it depends on
 * `@atlas-forms/player-components-react` and `@flow-studio/store`'s theme store — dependencies
 * the shared package deliberately does not have (see that package's own
 * `defaultCredentialFormRenderer.tsx`).
 *
 * The deleted `CredentialDialog.tsx` (recoverable verbatim via `git show
 * b17411167^:src/flow-studio/packages/flow-studio-designer/src/components/Modals/CredentialDialog.tsx`)
 * is the reference implementation this file ports from — both its 3-step Atlas Form resolution
 * strategy (`resolveAndLoadForm`) and its OAuth `window.postMessage` handshake (reused unchanged
 * from the still-present `OAuthButton.tsx`, which this same refactor orphaned but never deleted).
 */

import { useEffect, useState } from 'react';
import { FormRenderer as AtlasFormRenderer, FormThemeProvider } from '@atlas-forms/player-components-react';
import type { FormSchema as AtlasFormSchema } from '@atlas-forms/types-js';
import { useUIStore } from '@flow-studio/store';
import { apiConfig } from '@flow-studio/api';
import type {
  CredentialFormRenderer,
  CredentialFormRendererProps,
  CredentialOAuthConnect,
  CredentialTypeDTO,
} from '@bizfirst/ai-agent-admin-ui';
import { OAuthButton } from './OAuthButton';

async function fetchAtlasFormById(formId: number): Promise<AtlasFormSchema | null> {
  try {
    const token = localStorage.getItem('authToken');
    const resp = await fetch(`${apiConfig.baseUrl}/api/v1/atlas/forms/by-id`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ID: { ID: formId } }),
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    const schema = json?.data?.schema ?? json?.data?.Schema;
    return schema ? JSON.parse(schema) : null;
  } catch {
    return null;
  }
}

async function fetchAtlasFormByCode(formCode: string): Promise<AtlasFormSchema | null> {
  try {
    const token = localStorage.getItem('authToken');
    const resp = await fetch(`${apiConfig.baseUrl}/api/v1/atlas/forms/by-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ FormCode: formCode }),
    });
    if (!resp.ok) return null;
    const json = await resp.json();
    // search response — first matching form
    const form = Array.isArray(json?.data) ? json.data[0] : json?.data;
    const schema = form?.schema ?? form?.Schema;
    return schema ? JSON.parse(schema) : null;
  } catch {
    return null;
  }
}

/**
 * Three-step form resolution, ported verbatim from the deleted CredentialDialog.tsx's
 * `resolveAndLoadForm`:
 *  1. `acceptedType.editFormID` — node-level override
 *  2. `{CODE}_CredentialEditor` — naming convention
 *  3. `fallbackFormCode` from `CredentialTypeDTO.FieldsSchema`
 */
async function resolveAtlasForm(
  credentialType: CredentialTypeDTO,
  acceptedType: CredentialFormRendererProps['acceptedType']
): Promise<AtlasFormSchema | null> {
  if (acceptedType?.editFormID) {
    const schema = await fetchAtlasFormById(acceptedType.editFormID);
    if (schema) return schema;
  }

  const conventionCode = `${credentialType.Code}_CredentialEditor`;
  const conventionSchema = await fetchAtlasFormByCode(conventionCode);
  if (conventionSchema) return conventionSchema;

  if (credentialType.FieldsSchema) {
    try {
      const parsed = JSON.parse(credentialType.FieldsSchema) as { fallbackFormCode?: string };
      if (parsed.fallbackFormCode) {
        const fallback = await fetchAtlasFormByCode(parsed.fallbackFormCode);
        if (fallback) return fallback;
      }
    } catch {
      /* invalid JSON — skip */
    }
  }

  return null;
}

/**
 * Credential Atlas Forms are authored as self-contained forms — each one carries its own "name"/
 * "description" controls (see e.g. FormID 20703's `credential-basic-auth-editor` schema) because
 * historically (the deleted `CredentialDialog.tsx`) they were the ONLY form: that dialog rendered
 * no Name/Description fields of its own and instead destructured `{ name, description, ...rest }`
 * straight out of the Atlas Form's own values (`git show b17411167^:.../CredentialDialog.tsx`,
 * `handleSave`). `CredentialEditor.tsx` (the new shared component) does the opposite — Name/
 * Description are its own always-rendered top-level fields, and the backend's
 * `credentials/form-schema` endpoint already strips "name"/"description" out of the schema it
 * returns for exactly this reason. This renderer bypasses that endpoint (it resolves the raw Atlas
 * Form directly), so it must strip the same two controls itself, or the credential-type's own
 * name/description controls render a second time underneath `CredentialEditor`'s — confirmed live
 * (Basic Auth showed "Name"/"Description" twice, once from each source).
 */
function stripNameDescriptionControls(schema: AtlasFormSchema): AtlasFormSchema {
  const controls = (schema as any)?.controls;
  if (!Array.isArray(controls)) return schema;
  const filtered = controls.filter((c: any) => c?.id !== 'name' && c?.id !== 'description');
  if (filtered.length === controls.length) return schema;
  return { ...schema, controls: filtered } as AtlasFormSchema;
}

/**
 * CredentialFormRenderer implementation — resolves and renders the real Atlas Form for this
 * credential type instead of @bizfirst/ai-agent-admin-ui's plain-HTML `CredentialFormSchema.controls`.
 * `schema`/`mode` come from the extension-point contract; `schema` (the plain CredentialFormSchema)
 * is intentionally unused here — this renderer resolves its own Atlas Form schema instead.
 */
export const AtlasCredentialFormAdapter: CredentialFormRenderer = ({ credentialType, acceptedType, values, onFieldChange }) => {
  const { theme } = useUIStore();
  const [atlasSchema, setAtlasSchema] = useState<AtlasFormSchema | null>(null);
  const [resolving, setResolving] = useState(true);
  const [resolveError, setResolveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setResolving(true);
    setResolveError(null);
    setAtlasSchema(null);
    resolveAtlasForm(credentialType, acceptedType).then((schema) => {
      if (cancelled) return;
      setResolving(false);
      if (schema) {
        setAtlasSchema(stripNameDescriptionControls(schema));
      } else {
        setResolveError('No form found for this credential type. Please create the Atlas Form.');
      }
    });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [credentialType.CredentialTypeID, acceptedType?.editFormID]);

  if (resolving) {
    return <div className="aaa-form-group-full aaa-editor-loading">Loading form…</div>;
  }
  if (resolveError || !atlasSchema) {
    return <div className="aaa-form-group-full aaa-entity-dropdown-empty">{resolveError ?? 'No form available for this credential type.'}</div>;
  }

  return (
    // NOT "form-container" — that class is already ConnectorConfigDialog.css's heavy top-level
    // scrollable-body wrapper (16px padding, overflow-y:auto), unrelated to this nested use and
    // the actual cause of the "two separate boxes" look reported live (extra padding around the
    // Atlas-rendered fields, and none of the dark-theme label/input/focus overrides below applying
    // since those all target .atlas-form-container specifically).
    <div className="aaa-form-group-full atlas-form-container">
      <FormThemeProvider theme={theme}>
        <AtlasFormRenderer
          schema={atlasSchema}
          // Atlas Forms' own FormOperatingMode has no 'create' variant ('edit' | 'view' | 'design'
          // | 'admin' | 'preview') — the deleted CredentialDialog.tsx always passed a literal
          // "edit" here too, for both create and edit; ported verbatim rather than inventing a
          // create/edit distinction Atlas Forms itself doesn't have.
          mode="edit"
          initialValues={values}
          onValuesChange={(v: Record<string, unknown>) => {
            // Atlas Forms' FormRenderer reports the whole values object on every change; the
            // extension point's onFieldChange contract is single-field, so fan the full object
            // out field-by-field — equivalent end state to a whole-object replace since fieldValues
            // never holds keys outside this schema (name/description are handled separately by
            // CredentialEditor, not part of this Atlas Form's own field set).
            Object.keys(v).forEach((key) => onFieldChange(key, v[key]));
          }}
          nested
          enableCsrfProtection={false}
          showValidationSummary={false}
        />
      </FormThemeProvider>
    </div>
  );
};

/**
 * CredentialOAuthConnect implementation — reuses the still-present, previously-orphaned
 * OAuthButton.tsx's popup/postMessage handshake verbatim (unchanged from before the refactor).
 */
export const AtlasCredentialOAuthConnect: CredentialOAuthConnect = ({ credentialType, formValues, onExchanged }) => {
  const [oauthError, setOauthError] = useState<string | null>(null);

  const clientId = typeof formValues.clientId === 'string' ? formValues.clientId : '';
  const authorizationUrl = typeof formValues.authorizationUrl === 'string' ? formValues.authorizationUrl : '';
  const scopes = typeof formValues.scopes === 'string' ? formValues.scopes : '';
  const oauthReady = !!clientId.trim() && !!authorizationUrl.trim();

  const buildOAuthUrl = (): string => {
    if (!oauthReady) return '';
    const redirectUri = `${window.location.origin}/oauth/callback`;
    return (
      `${authorizationUrl}?response_type=code` +
      `&client_id=${encodeURIComponent(clientId)}` +
      `&redirect_uri=${encodeURIComponent(redirectUri)}` +
      `&scope=${encodeURIComponent(scopes)}`
    );
  };

  return (
    <div className="aaa-form-group-full oauth-section">
      <OAuthButton
        label={`Sign in with ${credentialType.Name}`}
        authUrl={buildOAuthUrl()}
        disabled={!oauthReady}
        onSuccess={(code, state) => {
          setOauthError(null);
          // Mirrors the deleted CredentialDialog.tsx's handleSave: _oauthCode/_redirectUri are
          // merged into the encrypted field bag; the backend exchanges the code for tokens.
          onExchanged({
            _oauthCode: code,
            _oauthState: state,
            _redirectUri: `${window.location.origin}/oauth/callback`,
          });
        }}
        onError={(err) => setOauthError(err)}
      />
      {oauthError && <p className="oauth-error">{oauthError}</p>}
      {!oauthReady && !oauthError && <p className="oauth-hint">Fill in Client ID and Authorization URL first</p>}
    </div>
  );
};
